import { useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { Features } from './components/Features';
import { ProductSection } from './components/ProductSection';
import { AboutSection } from './components/AboutSection';
import { StoreLocations } from './components/StoreLocations';
import { Footer } from './components/Footer';
import { FloatingWhatsApp } from './components/FloatingWhatsApp';
import { ProductDetailsPage } from './components/ProductDetailsPage';
import { PolicyPage } from './components/PolicyPage';
import { JamuPage } from './components/JamuPage';
import { CartDrawer } from './components/CartDrawer';
import { WishlistDrawer } from './components/WishlistDrawer';
import { MobileNav } from './components/MobileNav';
import { Product, CartItem } from './types';
import { PRODUCTS } from './constants';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';

type ViewState = 
  | { type: 'home' }
  | { type: 'product'; product: Product }
  | { type: 'policy'; section: 'shipping' | 'refund' }
  | { type: 'jamu' };

const App = () => {
  const [view, setView] = useState<ViewState>({ type: 'home' });
  const [history, setHistory] = useState<ViewState[]>([{ type: 'home' }]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const navigateTo = (newView: ViewState, replace = false) => {
    // Sync with browser history
    if (!replace) {
      window.history.pushState({ view: newView, index: currentIndex + 1 }, '');
    } else {
      window.history.replaceState({ view: newView, index: currentIndex }, '');
    }

    if (replace) {
      const newHistory = history.slice(0, currentIndex + 1);
      newHistory[currentIndex] = newView;
      setHistory(newHistory);
      setView(newView);
    } else {
      const newHistory = history.slice(0, currentIndex + 1);
      newHistory.push(newView);
      setHistory(newHistory);
      setCurrentIndex(newHistory.length - 1);
      setView(newView);
    }
  };

  const goBack = () => {
    window.history.back();
  };

  const goForward = () => {
    window.history.forward();
  };

  // Sync with browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const state = event.state;
      if (state && state.view) {
        setView(state.view);
        setCurrentIndex(state.index);
      } else {
        // Default to home if no state
        setView({ type: 'home' });
        setCurrentIndex(0);
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    // Initial state
    window.history.replaceState({ view: { type: 'home' }, index: 0 }, '');

    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isWishlistOpen, setIsWishlistOpen] = useState(false);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [wishlist, setWishlist] = useState<Product[]>([]);
  const mainRef = useRef<HTMLElement>(null);

  // Load wishlist from localStorage on mount
  useEffect(() => {
    const savedWishlist = localStorage.getItem('age_life_wishlist');
    if (savedWishlist) {
      try {
        setWishlist(JSON.parse(savedWishlist));
      } catch (e) {
        console.error("Failed to parse wishlist", e);
      }
    }
  }, []);

  // Save wishlist to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('age_life_wishlist', JSON.stringify(wishlist));
  }, [wishlist]);

  // View transition animation
  useGSAP(() => {
    gsap.fromTo(mainRef.current, 
      { opacity: 0, y: 10 },
      { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' }
    );
  }, [view.type, (view as any).product?.id, (view as any).section]);

  const handleProductSelect = (product: Product) => {
    navigateTo({ type: 'product', product });
    setIsWishlistOpen(false);
  };

  const handleShopProduct = (productId: string) => {
    const product = PRODUCTS.find(p => p.id === productId);
    if (product) {
      handleProductSelect(product);
    }
  };

  const handleNavigate = (sectionId: string) => {
    navigateTo({ type: 'home' });
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 50);
  };

  const handlePolicyNavigate = (section: 'shipping' | 'refund') => {
    navigateTo({ type: 'policy', section });
  };

  const addToCart = (product: Product, quantity: number) => {
    setCartItems(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => 
          item.id === product.id 
            ? { ...item, quantity: item.quantity + quantity } 
            : item
        );
      }
      return [...prev, { ...product, quantity }];
    });
    setIsCartOpen(true);
  };

  const updateCartQuantity = (id: string, delta: number) => {
    setCartItems(prev => 
      prev.map(item => 
        item.id === id 
          ? { ...item, quantity: Math.max(1, item.quantity + delta) } 
          : item
      )
    );
  };

  const removeFromCart = (id: string) => {
    setCartItems(prev => prev.filter(item => item.id !== id));
  };

  const toggleWishlist = (product: Product) => {
    setWishlist(prev => {
      const exists = prev.find(p => p.id === product.id);
      if (exists) {
        return prev.filter(p => p.id !== product.id);
      }
      return [...prev, product];
    });
  };

  const handleContinueShopping = () => {
    setIsCartOpen(false);
    setIsWishlistOpen(false);
    navigateTo({ type: 'home' });
  };

  const isInWishlist = (productId: string) => wishlist.some(p => p.id === productId);

  return (
    <div className="min-h-screen bg-earth-50 text-gray-800 font-sans selection:bg-turmeric-500 selection:text-white pb-20 md:pb-0">
      <Header 
        onNavigate={handleNavigate} 
        onProductSelect={handleProductSelect}
        cartCount={cartItems.reduce((sum, item) => sum + item.quantity, 0)}
        wishlistCount={wishlist.length}
        onCartClick={() => setIsCartOpen(true)}
        onWishlistClick={() => setIsWishlistOpen(true)}
        canGoBack={currentIndex > 0}
        canGoForward={currentIndex < history.length - 1}
        onBack={goBack}
        onForward={goForward}
      />
      
      <CartDrawer 
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        onUpdateQuantity={updateCartQuantity}
        onRemoveItem={removeFromCart}
        onContinueShopping={handleContinueShopping}
      />

      <WishlistDrawer
        isOpen={isWishlistOpen}
        onClose={() => setIsWishlistOpen(false)}
        wishlistItems={wishlist}
        onToggleWishlist={toggleWishlist}
        onAddToCart={(product) => addToCart(product, 1)}
        onViewProduct={handleProductSelect}
      />

      <main ref={mainRef}>
        {view.type === 'product' ? (
          <ProductDetailsPage 
            product={view.product} 
            onBack={() => navigateTo({ type: 'home' })}
            onAddToCart={addToCart}
            onToggleWishlist={toggleWishlist}
            isWishlisted={isInWishlist(view.product.id)}
          />
        ) : view.type === 'policy' ? (
          <PolicyPage 
            initialSection={view.section} 
            onBack={() => navigateTo({ type: 'home' })} 
          />
        ) : view.type === 'jamu' ? (
          <JamuPage 
            onBack={() => navigateTo({ type: 'home' })} 
          />
        ) : (
          <>
            <Hero 
              onLearnMore={() => navigateTo({ type: 'jamu' })} 
              onShopProduct={handleShopProduct}
            />
            <Features />
            <ProductSection 
              onProductSelect={handleProductSelect} 
              onAddToCart={addToCart}
              onToggleWishlist={toggleWishlist}
              wishlist={wishlist.map(p => p.id)}
            />
            <AboutSection />
            <StoreLocations />
          </>
        )}
      </main>
      <Footer 
        onNavigate={handleNavigate}
        onNavigateToPolicy={handlePolicyNavigate} 
      />
      
      <MobileNav 
        onNavigate={handleNavigate}
        onCartClick={() => setIsCartOpen(true)}
        onWishlistClick={() => setIsWishlistOpen(true)}
        cartCount={cartItems.reduce((sum, item) => sum + item.quantity, 0)}
        wishlistCount={wishlist.length}
      />
      
      <FloatingWhatsApp />
    </div>
  );
};

const container = document.getElementById('root');
if (container) {
  // Use a global variable to persist the root across hot reloads/re-executions
  const root = (window as any)._reactRoot || createRoot(container);
  (window as any)._reactRoot = root;
  root.render(<App />);
}